import { ChangeEvent, FC, useState } from "react"
import {
  deleteWbRecode,
  getWbRecode,
  ShopInfo,
  updateWbStocks,
  uploadWbRecode,
} from "../../app/reducers/shopsSlice"
import { Button, Checkbox, Flex, Heading, Strong, Text } from "@radix-ui/themes"
import { NotificationService } from "../../features/NotificationService/NotificationService"
import { StatusInfo } from "../UpdateStoksIcon/StatusInfo/StatusInfo"
import { STATUS } from "../../utils/const"
import { Tip, TipType } from "../Tip/Tip"
import { getMessage } from "../../msgs/msgs"
import { formRecodeFile, parseWbRecode } from "./helpers"
import { LogButton } from "../LogButton/LogButton"
import BaseApi from "../../api/api"
import { StorageSelectControl } from "../StorageSelect/StorageSelectControl"
import { MARKETPLACE } from "@acc/shared"
import { Modal } from "../../features/Modal/Modal"
import { useAppSelector } from "../../app/hooks"
import { selectOrdersStore } from "../../app/reducers/ordersReducer"
import { ObjectRenderer } from "../ObjectViewer/ObjectViewer"
import { StocksTable } from "../ObjectViewer/ObjectTableViewer"

export type ShopComponentProps = {
  shopInfo: ShopInfo
  ident: string
}

export const ShopComponent: FC<ShopComponentProps> = ({ ident, shopInfo }) => {
  const { storages } = useAppSelector(selectOrdersStore)

  const [isOpenErrModal, setOpenErrModal] = useState(false)
  const [isUpdatePrices, setUpdatePrices] = useState(false)
  const [isUpdateStocks, setUpdateStocks] = useState(false)
  const [selectedStorageId, setSelectedStorages] = useState("")
  const [isOpenUpdateModal, setOpenUpdateModal] = useState(false)
  const [isOpenModalResetStocks, setOpenModalResetStocks] = useState(false)

  const [isReturnToPromo, setReturnToPromo] = useState(false)

  const {
    lastPricesUpdated,
    lastStocksUpdated,
    errors,
    status,
    isExistRecode,
    recodeFields,
    calcIdent,
  } = shopInfo

  return (
    <Flex direction="column" justify="center" gap="3" p="3">
      <Heading as="h3" size="4">
        Загрузка остатков и обновление цен
      </Heading>
      <Flex justify="center">
        {(status && (
          <Heading as="h6" size="3">
            статус: <StatusInfo status={status} />
          </Heading>
        )) ||
          null}

        {(errors?.length && (
          <div className="flex gap-20">
            <StatusInfo
              status={STATUS.WARN}
              value="При обновлении были пропущены ошибки"
            />
            <Button color="red" onClick={() => setOpenErrModal(true)}>
              посмотреть ошибки
            </Button>
          </div>
        )) ||
          null}
      </Flex>

      <Flex direction="column" justify="center">
        {lastPricesUpdated && (
          <Strong>
            Последнее обновление цен:{" "}
            {new Date(lastPricesUpdated).toLocaleString()}
          </Strong>
        )}
        {lastStocksUpdated && (
          <Strong>
            Последнее обновление остатков:{" "}
            {new Date(lastStocksUpdated).toLocaleString()}
          </Strong>
        )}
      </Flex>

      <Flex gap="4" align="center" justify="center">
        {isExistRecode ? (
          <Flex gap="2" direction="column">
            <Button onClick={() => deleteWbRecode(ident)}>
              Удалить файл перекодировки
            </Button>
            <Button
              onClick={() => {
                getWbRecode(ident).then((rows) => {
                  formRecodeFile(rows, recodeFields, ident)
                })
              }}
            >
              Скачать перекодировку
            </Button>
          </Flex>
        ) : (
          <Button asChild>
            <label htmlFor={`${ident}-recode-file`}>
              Загрузить файл перекодировки
              <input
                style={{ display: "none" }}
                onChange={(e: ChangeEvent<HTMLInputElement>) => {
                  const file = e.target.files?.[0]
                  if (file) {
                    parseWbRecode(file, recodeFields).then((recode) =>
                      uploadWbRecode(ident, recode),
                    )
                  }
                }}
                type="file"
                accept=".xlsx"
                id={`${ident}-recode-file`}
              />
            </label>
          </Button>
        )}

        <RecodeTip
          fields={recodeFields}
          withSizes={["with_sizes_1"].includes(calcIdent || "")}
        />
      </Flex>

      <Flex direction="column" gap="3" justify="center">
        <StorageSelectControl
          label="Склад для обновления"
          value={selectedStorageId}
          onChange={(value) => {
            setSelectedStorages(value)
          }}
          controlKey="storages-select"
          marketplace={MARKETPLACE.WB}
        />

        <Flex justify="center" gap="2">
          <Text as="label" size="2">
            <Flex gap="2">
              <Checkbox
                value="updatePrices"
                checked={isUpdatePrices}
                onCheckedChange={(value) => setUpdatePrices(value === true)}
              />
              Обновить цены
            </Flex>
          </Text>
          <Text as="label" size="2">
            <Flex gap="2">
              <Checkbox
                value="updateStocks"
                checked={isUpdateStocks}
                onCheckedChange={(value) => setUpdateStocks(value === true)}
              />
              Обновить остатки
            </Flex>
          </Text>
          <Text as="label" size="2">
            <Flex gap="2">
              <Checkbox
                value="isReturnToPromo"
                checked={isReturnToPromo}
                onCheckedChange={(value) => setReturnToPromo(value === true)}
              />
              Вернуть в акцию
            </Flex>
          </Text>
        </Flex>

        <Flex justify="center" gap="2">
          <Button
            disabled={
              !isExistRecode ||
              !selectedStorageId ||
              (!isUpdatePrices && !isUpdateStocks && !isReturnToPromo)
            }
            onClick={() => setOpenUpdateModal(true)}
          >
            Обновить
          </Button>

          <Button
            disabled={!isExistRecode || !selectedStorageId}
            onClick={() => setOpenModalResetStocks(true)}
            color="red"
            variant="soft"
          >
            Сбросить остатки на складе
          </Button>

          <LogButton
            getData={() => {
              return BaseApi.logByShopIdent(ident).then((res) => {
                if (res.success) return res.data
                return []
              })
            }}
          />
        </Flex>
      </Flex>

      <Modal
        isOpen={isOpenUpdateModal}
        header={`Подтвердите действие. Обновление на ${ident}`}
        content={`Обновить ${isUpdatePrices ? "цены" : ""} ${
          isUpdatePrices && isUpdateStocks ? "и" : ""
        } ${isUpdateStocks ? "остатки" : ""} ${
          isReturnToPromo ? "вернуть в акцию" : ""
        } для склада ${
          storages.find((el) => el.id === +selectedStorageId)?.name
        }`}
        onClose={() => setOpenUpdateModal(false)}
        onOk={() => {
          selectedStorageId &&
            updateWbStocks({
              shopIdent: ident,
              selectedStorageId,
              updatePrices: isUpdatePrices,
              updateStocks: isUpdateStocks,
              isReturnToPromo,
            })
          setOpenUpdateModal(false)
        }}
      />
      <Modal
        isOpen={isOpenModalResetStocks}
        header={`Подтвердите действие. Сбросить остатки на ${ident}`}
        content={`Сбросить остатки для склада ${
          storages.find((el) => el.id === +selectedStorageId)?.name
        }`}
        onClose={() => setOpenModalResetStocks(false)}
        onOk={() => {
          if (selectedStorageId) {
            BaseApi.resetWbStocks(selectedStorageId, ident).then((res) => {
              if (res.data) {
                NotificationService.showNotification({
                  message: res.data.message,
                  level: (res.data.success && "success") || "error",
                })
              }
            })
          }
          setOpenModalResetStocks(false)
        }}
      />
      <Modal
        isOpen={isOpenErrModal}
        header="Ошибки пропущенные при обновлении"
        onClose={() => setOpenErrModal(false)}
        onOk={() => setOpenErrModal(false)}
        size="auto"
        content={
          <div className="flex flex-column gap-20">
            {errors?.map((el) => {
              if (el.type === "UPLOAD_STOCKS") {
                return <StocksTable data={el} />
              }
              return (
                <div className="flex gap-20">
                  <b>{getMessage(el.type)}</b>
                  <ObjectRenderer data={el.errors} />
                </div>
              )
            })}
          </div>
        }
      ></Modal>
    </Flex>
  )
}

const RecodeTip: FC<{ fields: string[]; withSizes?: boolean }> = ({
  fields,
  withSizes,
}) => {
  return (
    <Tip
      type={TipType.WARNING}
      content={
        <span>
          <b>Внимание! </b>
          <p>
            Файл перекодировки должен быть формата <b> XLSX </b> и содержать
            следующие <b> обязательные </b> колонки:
          </p>
          <ol style={{ textAlign: "left" }}>
            {fields.map((el) => {
              return (
                <li key={el}>
                  <Strong>{el} </Strong> -{" "}
                  <Text>{getMessage(`${el}_description`)}</Text>
                </li>
              )
            })}
            {withSizes && (
              <li>
                <Strong>sizes </Strong> - размеры упаковки, в см. В
                формате:"10;10;10";
              </li>
            )}
          </ol>
        </span>
      }
    />
  )
}
